/**
 * Cliente do catálogo público de uma vaga — `GET /api/public/catalog/:token`,
 * `POST …/signal` (Story 23.20) e `POST …/feedback` (Story 23.25).
 *
 * ⚠️ ESTE É O ÚNICO CLIENTE DE API DO PRODUTO QUE NÃO MANDA CREDENCIAIS, e é de
 * propósito: as rotas do outro lado estão montadas ANTES da barreira
 * `requireAuth`, e quem abre o link não tem — nem devia ter — sessão nenhuma.
 * Mandar `credentials: 'include'` aqui não daria erro; daria pior, que era um
 * pedido que se comporta de uma maneira para quem tem sessão da Genesis aberta
 * noutro separador e de outra para o cliente que recebeu o link. O portão é o
 * TOKEN — também na escrita.
 *
 * As escritas são DUAS, e estreitas:
 *   • o sinal do cliente sobre um perfil ("Tenho interesse" / "Descartar"). O
 *     autor é o cliente, e o servidor identifica-o pelo tipo do evento, como no
 *     portal. Sem `note`: texto livre de um visitante anónimo na linha do tempo
 *     interna é um vetor de abuso que ninguém pediu (contrato da 23.20);
 *   • o RETORNO DA SELEÇÃO, sem corpo: o servidor lê os sinais que tem e manda o
 *     e-mail à equipe da vaga e à Genesis Consulting (contrato da 23.25).
 */
import { API_BASE } from '@/lib/api'
import { ApiError } from '@/lib/api/users'
import { parseProblemJson, readProblemMessage } from '@/lib/api/problem'

export { ApiError }

/** Uma habilidade avaliada. Espelha o que `lib/showcase-profile.ts` serializa. */
export interface PublicCatalogSkillScore {
  label: string
  /** 0–10. */
  score: number
  weight: number
}

/**
 * Uma competência comportamental (Story 17.5). Espelha o mesmo serializador.
 *
 * ⚠️ Sem `rationale` e sem `citations` — e aqui a razão é evidente: quem lê esta
 * página é anónimo, com um token nas mãos. `anchor` é texto de rubrica (a
 * descrição do nível, ou o motivo quando não houve nível), igual para todos os
 * perfis daquele nível.
 */
export interface PublicCatalogBehavioralCompetency {
  label: string
  /** 1 a 4; `null` = não observada. */
  level: 1 | 2 | 3 | 4 | null
  anchor: string
}

/**
 * Um perfil do catálogo.
 *
 * ⚠️ Estruturalmente idêntico ao `ShowcaseProfile` de
 * `components/showcase-profile-card` e ao `PortalShowcaseProfile` do portal —
 * porque é literalmente a mesma resposta, montada pela mesma whitelist do
 * servidor. É essa identidade que faz a página pública mostrar «exatamente o
 * mesmo catálogo» sem uma conversão pelo meio.
 *
 * `codename` é a única forma de nomear a pessoa. Não há — nem vai haver — nome,
 * contacto, citação ou evidência: a garantia vive na forma dos dados do lado do
 * servidor, não num filtro desta camada.
 */
export interface PublicCatalogProfile {
  applicationId: string
  codename: string
  headline: string | null
  finalScore: number | null
  behavioralCompetencies: PublicCatalogBehavioralCompetency[]
  skillScores: PublicCatalogSkillScore[]
  coverageGaps: string[]
  summary: string | null
  strengths: string[]
  development: string[]
  /** ISO-8601. É por ele que o servidor ordena — a página não reordena. */
  presentedAt: string | null
  /**
   * O ÚLTIMO sinal do cliente sobre este perfil (Story 23.20); `null` = ninguém
   * sinalizou ainda. É estado partilhado: quem tiver o link vê o mesmo.
   */
  clientSignal: PublicCatalogClientSignal | null
}

/** O gesto do cliente sobre um perfil. Mudar de ideias é um sinal novo. */
export type PublicCatalogClientSignal = 'interested' | 'declined'

/** O corpo do `POST …/signal` — exactamente estes dois campos, e nenhum outro. */
export interface PublicCatalogSignalInput {
  applicationId: string
  signal: PublicCatalogClientSignal
}

/** A resposta do `POST …/signal`: 201 quando grava, 200 quando o último já era igual. */
export interface PublicCatalogSignalReceipt {
  applicationId: string
  clientSignal: PublicCatalogClientSignal
}

/**
 * A resposta completa.
 *
 * ⚠️ `opportunity` leva SÓ o título. Sem id, sem cliente, sem descrição — o
 * servidor não os manda (decisão D2 da story 16.1), e um teste da whitelist
 * garante-o. Se um dia aparecerem aqui, o erro está a montante.
 */
export interface PublicCatalog {
  opportunity: { title: string }
  profiles: PublicCatalogProfile[]
  /** ISO-8601 do último retorno da seleção enviado (Story 23.25); `null` = nenhum. */
  feedbackSentAt: string | null
}

/** A resposta 200 do `POST …/feedback`: o instante em que o e-mail saiu. */
export interface PublicCatalogFeedbackReceipt {
  feedbackSentAt: string
}

/** A mensagem de recuo, por status. O 404 é o caso frequente e tem nome próprio. */
function fallback(status: number): string {
  if (status === 404) return 'Link inválido ou revogado.'
  return 'Não foi possível carregar este catálogo.'
}

/**
 * Lê o catálogo de um token.
 *
 * O `signal` vem do TanStack Query — cancela o pedido quando a página é
 * desmontada a meio.
 *
 * Um 429 é o `ipRateLimit` do mount (200 por hora por visitante, Story 23.32), e
 * o `detail` dele vem do servidor em inglês. A frase é escrita aqui, em pt-BR, e
 * não promete um minuto: a espera pode ir até ao fim da hora (QA da 23.32,
 * REL-001). Os outros erros continuam a ler o `detail`.
 */
export async function getPublicCatalog(
  token: string,
  options: { signal?: AbortSignal } = {},
): Promise<PublicCatalog> {
  // O `signal` entra por espalhamento condicional e não como `signal:
  // options.signal`: com `exactOptionalPropertyTypes`, um `undefined` explícito
  // não é o mesmo que a chave ausente, e o `RequestInit` só aceita
  // `AbortSignal | null`.
  const res = await fetch(`${API_BASE}/public/catalog/${encodeURIComponent(token)}`, {
    ...(options.signal ? { signal: options.signal } : {}),
  })

  if (res.status === 429) {
    throw new ApiError(
      429,
      'Muitos acessos a este link em pouco tempo. Aguarde um pouco e tente de novo.',
    )
  }
  if (!res.ok) {
    throw new ApiError(res.status, await readProblemMessage(res, fallback(res.status)))
  }

  return (await res.json()) as PublicCatalog
}

/**
 * A frase do erro de um sinal, por status — SEMPRE em pt-BR, e escrita aqui.
 *
 * ⚠️ NÃO se lê o `detail` do servidor, ao contrário do GET: o 404 desta rota é o
 * mesmo "link inválido ou revogado" para um perfil que saiu do catálogo entre o
 * carregamento e o clique, e a frase do 429 (o limitador da rota) não tem
 * contrato de língua. Quem lê isto é alguém de fora, a meio de um gesto — a
 * frase tem de dizer o que aconteceu com a escolha dele, na língua dele.
 */
export function publicSignalErrorMessage(status: number | null): string {
  if (status === 404) {
    return 'Não foi possível registrar: este perfil saiu do catálogo ou o link foi desativado.'
  }
  if (status === 429) return 'Muitas marcações em pouco tempo. Aguarde um pouco e tente de novo.'
  return 'Não foi possível registrar sua escolha. Tente de novo em instantes.'
}

/**
 * Grava o sinal do cliente sobre um perfil do catálogo (Story 23.20).
 *
 * Sem credenciais, como o GET — ver o cabeçalho. Um erro de rede (o `fetch`
 * rejeita, sem status) também sai como `ApiError`, para quem chama ter UMA forma
 * de ler a frase.
 */
export async function sendPublicCatalogSignal(
  token: string,
  input: PublicCatalogSignalInput,
): Promise<PublicCatalogSignalReceipt> {
  let res: Response
  try {
    res = await fetch(`${API_BASE}/public/catalog/${encodeURIComponent(token)}/signal`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      // Os dois campos, montados à mão: espalhar `input` deixaria passar o que
      // um chamador lá pusesse a mais — e o contrato recusa `note`.
      body: JSON.stringify({ applicationId: input.applicationId, signal: input.signal }),
    })
  } catch {
    throw new ApiError(0, publicSignalErrorMessage(null))
  }

  if (!res.ok) throw new ApiError(res.status, publicSignalErrorMessage(res.status))

  return (await res.json()) as PublicCatalogSignalReceipt
}

/**
 * A frase do erro do retorno da seleção — pt-BR, escrita aqui, pelo mesmo motivo
 * do `publicSignalErrorMessage`: quem lê é alguém de fora, e não se lê o `detail`.
 *
 * O `code` (na raiz do problem+json) separa as respostas do mesmo status:
 *   • 409 `no_signals` (nenhum perfil marcado) e `no_recipients` (a vaga não tem
 *     e-mails, nem contacto, nem quem a criou);
 *   • 429 `cooldown` (a pausa de 5 minutos da vaga) e `daily_limit` (o teto
 *     diário de retornos da vaga);
 *   • 422 `too_many_recipients` (a vaga tem mais de 50 e-mails).
 * Nos que não se resolvem a repetir, a frase manda falar com a Genesis, e não
 * "tentar de novo".
 *
 * ⚠️ A rota não tem limite PRÓPRIO por IP (QA da 23.25). Mas o `ipRateLimit` do
 * mount de `/api/public/catalog/*` (200 por hora por visitante, Story 23.32) pode
 * responder 429 sem `code` (QA da 23.26, DOC-001). É para ele a frase genérica,
 * que não promete um minuto: a espera pode ir até ao fim da hora.
 */
export function publicFeedbackErrorMessage(status: number | null, code: string | null): string {
  if (status === 409 && code === 'no_signals') {
    return 'Marque pelo menos um perfil antes de enviar o retorno.'
  }
  if (status === 409 && code === 'no_recipients') {
    return 'Não foi possível enviar: esta vaga não tem destinatários cadastrados. Fale com a Genesis Consulting.'
  }
  if (status === 429 && code === 'cooldown') {
    return 'Um retorno acabou de ser enviado. Aguarde alguns minutos para enviar outro.'
  }
  if (status === 429 && code === 'daily_limit') {
    return 'Esta vitrine já enviou muitos retornos hoje. Tente amanhã ou fale com a Genesis Consulting.'
  }
  if (status === 429) return 'Muitas tentativas em pouco tempo. Aguarde um pouco e tente de novo.'
  if (status === 422 && code === 'too_many_recipients') {
    return 'Não foi possível enviar: esta vaga tem destinatários demais. Fale com a Genesis Consulting.'
  }
  if (status === 404) {
    return 'Não foi possível enviar: este link foi desativado. Peça um novo à Genesis Consulting.'
  }
  if (status === 502) {
    return 'O e-mail do retorno não saiu. As marcações continuam registradas: tente enviar de novo em instantes.'
  }
  return 'Não foi possível enviar o retorno. Tente de novo em instantes.'
}

/**
 * Envia o retorno da seleção (Story 23.25): SEM corpo — o servidor lê o último
 * sinal de cada perfil no momento do envio — e sem credenciais, como o resto.
 */
export async function sendPublicCatalogFeedback(
  token: string,
): Promise<PublicCatalogFeedbackReceipt> {
  let res: Response
  try {
    res = await fetch(`${API_BASE}/public/catalog/${encodeURIComponent(token)}/feedback`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
    })
  } catch {
    throw new ApiError(0, publicFeedbackErrorMessage(null, null))
  }

  if (!res.ok) {
    const corpo = await parseProblemJson(res)
    const code = typeof corpo?.code === 'string' ? corpo.code : null
    throw new ApiError(res.status, publicFeedbackErrorMessage(res.status, code))
  }

  return (await res.json()) as PublicCatalogFeedbackReceipt
}
